import React, { useEffect, useRef, useCallback } from 'react';
import { Grid, Paper, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BainCard from './BainCard';
import { usePresence } from '../../hooks/usePresence';
import { useSondeContext } from '../../context/SondeContext';
import { webSocketService } from '../../services/WebSocketService';

// Types déjà définis dans ApiService, importés via le contexte

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { bains: boitiers, loading, error, refreshData } = useSondeContext();
  // Présence temps réel : indépendante du cycle de rafraîchissement des
  // mesures (30 s), qui serait bien trop lent pour un seuil à 10 s.
  const { presence, supervision, lienServeur } = usePresence();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const refreshDataRef = useRef(refreshData);

  // Garder la ref à jour pour éviter les closures stales
  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);

  // Un boitier est un boitier : une seule route, portee par son id.
  // L'ancien aiguillage devinait la famille en cherchant « rincage » ou
  // « niveau » dans le topic — deux heuristiques a tenir d'accord, et un
  // boitier au topic hors convention atterrissait sur la mauvaise page.
  const handleBainClick = (boitierId: number) => {
    navigate(`/boitier/${boitierId}`);
  };

  // Fonction pour démarrer le rafraîchissement automatique (fallback quand WS déconnecté)
  const startAutoRefresh = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Timer de fallback (30s) — le WebSocket fait le temps réel, ce polling est un filet de sécurité
    intervalRef.current = setInterval(() => {
      if (!webSocketService.isConnectedToServer()) {
        refreshDataRef.current();
      }
    }, 30000);
  };

  // Fonction pour arrêter le rafraîchissement automatique
  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Charger les données au montage initial du Dashboard
  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter seulement au montage

  // Démarrer le rafraîchissement automatique seulement après le chargement initial
  useEffect(() => {
    // Ne démarrer l'auto-refresh que si les données sont chargées
    if (!loading && boitiers.length > 0) {
      startAutoRefresh();
    }
    
    // Nettoyer les timers au démontage du composant
    return () => {
      stopAutoRefresh();
    };
  }, [loading]); // Dépendance sur loading pour démarrer après le premier chargement


  if (loading && boitiers.length === 0) {
    return (
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Tableau de bord - Surveillance des boîtiers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Tableau de bord - Surveillance des boîtiers
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/*
        LOT 7.4 — un bandeau distinct pour chacune des deux causes d'absence
        d'information.

        Ce tableau de bord ne portait AUCUN de ces deux bandeaux. Quand la
        base tombait, `/presence` rendait 200 avec zero boitier et l'ecran
        restait strictement identique a un parc en bonne sante. Quand le lien
        MQTT du serveur tombait, tous les boitiers passaient « indetermine »
        sans que rien n'explique pourquoi.

        Les deux bandeaux restent distincts, car la conduite a tenir differe.
        Base injoignable : la supervision n'a plus aucune information. Lien
        MQTT coupe : la base repond, mais plus aucun battement n'arrive.
      */}
      {supervision === 'indisponible' && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="bandeau-supervision-indisponible">
          <strong>Supervision indisponible.</strong> La base de données ne
          répond pas : l'état des boîtiers affiché ci-dessous est indéterminé
          et peut être périmé. Aucune alerte ne peut être calculée.
        </Alert>
      )}

      {supervision === 'ok' && !lienServeur && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="bandeau-lien-mqtt">
          <strong>Lien MQTT du serveur coupé.</strong> La supervision répond,
          mais elle ne reçoit plus les battements des boîtiers : leur présence
          ne peut pas être établie. Les dernières mesures enregistrées restent
          affichées.
        </Alert>
      )}

      <Grid container spacing={3}>
        {boitiers.map((boitier) => (
          <Grid item xs={12} sm={6} md={4} key={boitier.boitier_id}>
            <BainCard 
              boitier={boitier} 
              onClick={() => handleBainClick(boitier.boitier_id)} 
              presence={presence[boitier.boitier_id]}
              supervision={supervision}
            />
          </Grid>
        ))}
        
        {boitiers.length === 0 && !loading && !error && supervision === 'ok' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography>
                Aucun boîtier détecté. Vérifiez que les capteurs sont connectés.
              </Typography>
            </Paper>
          </Grid>
        )}

        {/*
          « Aucun boitier detecte » est une AFFIRMATION. Elle ne doit pas
          sortir quand la supervision ne sait pas repondre. Cette affirmation
          a masque une panne de base.
        */}
        {boitiers.length === 0 && !loading && supervision === 'indisponible' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography>
                Liste des boîtiers indisponible : la supervision n'a pas pu
                interroger sa base. Le nombre de boîtiers installés n'est pas
                connu.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
